import { useEffect, useRef, useState } from 'react';
import { CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Un período «desde – hasta» con atajos y un calendario: el primer clic marca el inicio y el
 * segundo el fin, con el tramo sombreado. Las fechas van y vienen como `AAAA-MM-DD`, igual que
 * las de un `<input type="date">`, y se calculan en hora local para que el día no se corra.
 */
export function SelectorDePeriodo({
  desde,
  hasta,
  alCambiar,
  desactivado = false,
}: {
  desde: string;
  hasta: string;
  alCambiar: (desde: string, hasta: string) => void;
  desactivado?: boolean;
}) {
  const [abierto, setAbierto] = useState(false);
  const [mes, setMes] = useState(() => primeroDe(aFecha(hasta)));
  const [inicio, setInicio] = useState<string | null>(null);
  const [encima, setEncima] = useState<string | null>(null);
  const caja = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) {
      return;
    }
    function fuera(evento: MouseEvent) {
      if (caja.current && !caja.current.contains(evento.target as Node)) {
        cerrar();
      }
    }
    function tecla(evento: KeyboardEvent) {
      if (evento.key === 'Escape') {
        cerrar();
      }
    }
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto]);

  function cerrar() {
    setAbierto(false);
    setInicio(null);
    setEncima(null);
  }

  function abrir() {
    setMes(primeroDe(aFecha(hasta)));
    setAbierto(true);
  }

  function elegir(dia: string) {
    if (inicio === null) {
      setInicio(dia);
      return;
    }
    const [a, b] = dia < inicio ? [dia, inicio] : [inicio, dia];
    alCambiar(a, b);
    cerrar();
  }

  function atajo(a: Date, b: Date) {
    alCambiar(aTexto(a), aTexto(b));
    cerrar();
  }

  // Mientras se elige, el tramo va del inicio a donde esté el ratón.
  const [tramoDesde, tramoHasta] =
    inicio === null
      ? [desde, hasta]
      : [encima && encima < inicio ? encima : inicio, encima && encima > inicio ? encima : inicio];

  const hoy = new Date();
  const dias = celdasDelMes(mes);

  return (
    <div ref={caja} className="relative">
      <button
        type="button"
        onClick={() => (abierto ? cerrar() : abrir())}
        disabled={desactivado}
        aria-expanded={abierto}
        aria-haspopup="dialog"
        className="flex h-9 items-center gap-2 rounded-lg border border-armazon-200 bg-white px-3 text-xs font-semibold text-armazon-700 transition-colors hover:border-marca-400 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <CalendarRange className="size-4 text-marca-600" aria-hidden />
        <span className="importe">{enCorto(desde)}</span>
        <span className="text-armazon-300">—</span>
        <span className="importe">{enCorto(hasta)}</span>
      </button>

      {abierto && (
        <div
          role="dialog"
          aria-label="Elegir el período"
          className="absolute left-0 top-11 z-40 flex overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-2xl shadow-armazon-900/10"
        >
          <div className="flex w-40 flex-col gap-0.5 border-r border-armazon-100 bg-armazon-50/70 p-2">
            <p className="px-2 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-wide text-armazon-400">
              Atajos
            </p>
            {ATAJOS.map(([rotulo, calcular]) => {
              const [a, b] = calcular(hoy);
              const activo = aTexto(a) === desde && aTexto(b) === hasta;
              return (
                <button
                  key={rotulo}
                  type="button"
                  onClick={() => atajo(a, b)}
                  className={`rounded-lg px-2 py-1.5 text-left text-[11px] font-semibold uppercase transition-colors ${
                    activo
                      ? 'bg-marca-600 text-white'
                      : 'text-armazon-600 hover:bg-white hover:text-marca-700'
                  }`}
                >
                  {rotulo}
                </button>
              );
            })}
          </div>

          <div className="w-72 p-3">
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}
                aria-label="Mes anterior"
                className="grid size-8 place-items-center rounded-lg text-armazon-500 hover:bg-armazon-100"
              >
                <ChevronLeft className="size-4" aria-hidden />
              </button>
              <span className="text-xs font-bold uppercase tracking-wide text-armazon-800">
                {MESES[mes.getMonth()]} {mes.getFullYear()}
              </span>
              <button
                type="button"
                onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}
                aria-label="Mes siguiente"
                className="grid size-8 place-items-center rounded-lg text-armazon-500 hover:bg-armazon-100"
              >
                <ChevronRight className="size-4" aria-hidden />
              </button>
            </div>

            <div className="grid grid-cols-7 text-center text-[10px] font-bold uppercase text-armazon-400">
              {SEMANA.map((d) => (
                <span key={d} className="py-1">
                  {d}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7" onMouseLeave={() => setEncima(null)}>
              {dias.map((dia, i) => {
                if (dia === null) {
                  return <span key={`vacio-${i}`} />;
                }
                const texto = aTexto(dia);
                const dentro = texto >= tramoDesde && texto <= tramoHasta;
                const extremo = texto === tramoDesde || texto === tramoHasta;
                const esHoy = texto === aTexto(hoy);
                return (
                  <button
                    key={texto}
                    type="button"
                    onClick={() => elegir(texto)}
                    onMouseEnter={() => setEncima(texto)}
                    className={`importe my-0.5 h-8 text-xs transition-colors ${
                      extremo
                        ? 'rounded-lg bg-marca-600 font-bold text-white'
                        : dentro
                          ? 'bg-marca-50 text-marca-800'
                          : 'rounded-lg text-armazon-700 hover:bg-armazon-100'
                    } ${esHoy && !extremo ? 'font-bold text-marca-700 underline' : ''}`}
                  >
                    {dia.getDate()}
                  </button>
                );
              })}
            </div>

            <p className="mt-2 border-t border-armazon-100 pt-2 text-center text-[10px] uppercase text-armazon-400">
              {inicio === null
                ? 'Pulse el primer día y luego el último'
                : `Desde el ${enCorto(inicio)}: ahora el último día`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const SEMANA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

const ATAJOS: ReadonlyArray<readonly [string, (hoy: Date) => readonly [Date, Date]]> = [
  ['Este mes', (h) => [new Date(h.getFullYear(), h.getMonth(), 1), h]],
  [
    'Mes anterior',
    (h) => [
      new Date(h.getFullYear(), h.getMonth() - 1, 1),
      new Date(h.getFullYear(), h.getMonth(), 0),
    ],
  ],
  ['Últimos 3 meses', (h) => [new Date(h.getFullYear(), h.getMonth() - 2, 1), h]],
  ['Este año', (h) => [new Date(h.getFullYear(), 0, 1), h]],
  [
    'Año anterior',
    (h) => [new Date(h.getFullYear() - 1, 0, 1), new Date(h.getFullYear() - 1, 11, 31)],
  ],
];

/** Los días del mes, con huecos delante para que el 1 caiga en su día de la semana (lunes primero). */
function celdasDelMes(primero: Date): Array<Date | null> {
  const hueco = (primero.getDay() + 6) % 7;
  const cuantos = new Date(primero.getFullYear(), primero.getMonth() + 1, 0).getDate();
  return [
    ...Array<null>(hueco).fill(null),
    ...Array.from(
      { length: cuantos },
      (_, i) => new Date(primero.getFullYear(), primero.getMonth(), i + 1),
    ),
  ];
}

function primeroDe(fecha: Date): Date {
  return new Date(fecha.getFullYear(), fecha.getMonth(), 1);
}

function aFecha(texto: string): Date {
  const [anio, mes, dia] = texto.split('-').map(Number);
  return new Date(anio ?? 1970, (mes ?? 1) - 1, dia ?? 1);
}

function aTexto(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

function enCorto(texto: string): string {
  const [anio, mes, dia] = texto.split('-');
  return `${dia}/${mes}/${anio}`;
}
